import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ChevronRight from "@mui/icons-material/ChevronRight";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { Box, CircularProgress, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { KeyboardEvent, useCallback, useState } from "react";

import type { ScoutTodoItem, ScoutTodoSnapshot } from "./scoutTodoSnapshot";

import { formatTodoItemLabel, formatTodoItemResult } from "./customerFacingCopy";

const MAX_RESULT_CHARS = 120;

function truncateText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars - 1)}…`;
}

function formatSummary(snapshot: ScoutTodoSnapshot): string {
  const { summary } = snapshot;
  const completed = summary.done + summary.skipped;
  if (summary.total === 0) {
    return snapshot.isPlanning ? "Planning steps…" : "No steps yet";
  }
  if (completed === summary.total) {
    return `${summary.total} step${summary.total === 1 ? "" : "s"} complete`;
  }
  if (summary.in_progress > 0) {
    return `${completed} of ${summary.total} complete · ${summary.in_progress} in progress`;
  }
  const remaining = summary.pending + summary.in_progress;
  return `${completed} of ${summary.total} complete · ${remaining} remaining`;
}

function TodoStatusIcon({ status }: { status: ScoutTodoItem["status"] }) {
  const theme = useTheme();

  if (status === "in_progress") {
    return (
      <CircularProgress
        size={16}
        thickness={5}
        aria-hidden
        sx={{ color: theme.palette.primary.main, flexShrink: 0 }}
      />
    );
  }
  if (status === "done") {
    return (
      <CheckCircleOutline
        fontSize="small"
        aria-hidden
        sx={{ color: theme.palette.success.main, flexShrink: 0 }}
      />
    );
  }
  if (status === "skipped") {
    return (
      <RemoveCircleOutline
        fontSize="small"
        aria-hidden
        sx={{ color: "text.disabled", flexShrink: 0 }}
      />
    );
  }
  return (
    <RadioButtonUnchecked
      fontSize="small"
      aria-hidden
      sx={{ color: "text.disabled", flexShrink: 0 }}
    />
  );
}

function TodoRow({ item }: { item: ScoutTodoItem }) {
  const theme = useTheme();
  const isActive = item.status === "in_progress";
  const isComplete = item.status === "done";
  const isSkipped = item.status === "skipped";
  const displayLabel = formatTodoItemLabel(item);
  const displayResult = formatTodoItemResult(item);

  const labelColor = (() => {
    if (isActive) return "text.primary";
    if (isComplete || isSkipped) return "text.secondary";
    return "text.primary";
  })();

  const rowBg = isActive ? alpha(theme.palette.primary.main, 0.06) : "transparent";

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        bgcolor: rowBg,
      }}
    >
      <Box sx={{ mt: 0.125 }}>
        <TodoStatusIcon status={item.status} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: labelColor,
            fontWeight: isActive ? 500 : 400,
            textDecoration: isSkipped ? "line-through" : "none",
          }}
        >
          {displayLabel}
        </Typography>
        {displayResult && (isComplete || isSkipped) ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {truncateText(displayResult, MAX_RESULT_CHARS)}
          </Typography>
        ) : null}
        {isSkipped && !displayResult ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            Skipped
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

export function ScoutTodoList({
  snapshot,
  isStreaming = false,
}: {
  snapshot: ScoutTodoSnapshot;
  isStreaming?: boolean;
}) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((current) => !current);
  }, []);

  const handleHeaderKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleExpanded();
      }
    },
    [toggleExpanded]
  );

  if (!snapshot.hasChecklist) {
    return null;
  }

  const summaryText = formatSummary(snapshot);

  return (
    <Box
      role="region"
      aria-label="Scout checklist"
      aria-busy={snapshot.isPlanning || isStreaming}
      sx={{
        mt: 1,
        mb: 1,
        px: 1.25,
        py: 1,

        minWidth: { xs: "100%", sm: 720 },
        width: "100%",
        borderRadius: 1.5,
        border: 1,
        borderColor: alpha(theme.palette.divider, 0.9),
        bgcolor: alpha(theme.palette.background.paper, 0.6),
      }}
    >
      <Stack spacing={0.75}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label="Toggle Scout checklist"
          onClick={toggleExpanded}
          onKeyDown={handleHeaderKeyDown}
          sx={{
            minHeight: 28,
            cursor: "pointer",
            userSelect: "none",
            borderRadius: 1,
            mx: -0.5,
            px: 0.5,
            "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.04) },
          }}
        >
          <IconButton
            size="small"
            aria-hidden
            tabIndex={-1}
            sx={{
              p: 0.25,
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            spacing={1}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
              Scout&apos;s checklist
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
            >
              {summaryText}
            </Typography>
          </Stack>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {snapshot.isPlanning ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, px: 1 }}>
              <CircularProgress size={14} thickness={5} aria-hidden />
              <Typography variant="body2" color="text.secondary">
                Planning the steps…
              </Typography>
            </Stack>
          ) : snapshot.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
              Waiting for the checklist…
            </Typography>
          ) : (
            <Stack spacing={0.25} aria-live="polite" aria-atomic="false">
              {snapshot.items.map((item) => (
                <TodoRow key={item.id} item={item} />
              ))}
            </Stack>
          )}
        </Collapse>
      </Stack>
    </Box>
  );
}
